// Shared luxury theme for Granipal vitrine pages
// Aligned with HomePage palette (copper / cream / cocoa)

export const COPPER       = '#B8895A';
export const COPPER_LIGHT = '#D4A878';
export const COPPER_DARK  = '#8B5E2F';
export const INK          = '#2B1C12';
export const INK_SOFT     = '#6B5340';
export const CREAM        = '#F5E6D0';
export const BEIGE        = '#D9B583';
export const DARK_BG      = '#120B07';
export const DARK_BG_2    = '#1A130D';

// Display font for GRANIPAL/H2 — DM Serif Display (high-contrast Didone modern luxe)
export const DISPLAY_FF   = "'DM Serif Display', 'Fraunces', 'Cormorant Garamond', serif";
export const ITALIC_FF    = "'Cormorant Garamond', 'Playfair Display', serif";

export const COPPER_GRAD  = `linear-gradient(135deg, ${COPPER_LIGHT} 0%, ${COPPER} 45%, ${COPPER_DARK} 100%)`;

// SVG paper grain
const GRAIN_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="220" height="220">' +
    '<filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch"/>' +
    '<feColorMatrix type="saturate" values="0"/></filter>' +
    '<rect width="100%" height="100%" filter="url(#n)" opacity="0.55"/></svg>'
);
export const GRAIN_BG = `url("data:image/svg+xml,${GRAIN_SVG}")`;

// === Reusable luxe components ===

export function CopperHalo({ x = '50%', y = '50%', size = 700, opacity = 0.10 }) {
  return (
    <div
      aria-hidden
      className="absolute pointer-events-none"
      style={{
        left: x, top: y, width: size, height: size, transform: 'translate(-50%, -50%)',
        background: `radial-gradient(circle, rgba(184,137,90,${opacity}) 0%, rgba(184,137,90,0) 65%)`,
        filter: 'blur(20px)',
      }}
    />
  );
}

// Rich dark background — adds copper sheens, bronze tints, soft luminous depth
// Place INSIDE any dark section as a sibling absolute overlay.
export function RichDarkLayer() {
  return (
    <>
      {/* Multi-layer copper gradient sheen for depth */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 15% 15%, rgba(184,137,90,0.08) 0%, transparent 40%),' +
            'radial-gradient(ellipse at 85% 85%, rgba(212,168,120,0.06) 0%, transparent 45%),' +
            'radial-gradient(ellipse at 50% 50%, rgba(139,94,47,0.04) 0%, transparent 60%)',
        }}
      />
      {/* Vertical bronze sheen */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(184,137,90,0.06) 0%, transparent 18%, transparent 82%, rgba(139,94,47,0.08) 100%)',
        }}
      />
      {/* Grain */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-overlay"
        style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }}
      />
    </>
  );
}

// Glass card style (apply as style prop)
export const GLASS_STYLE = {
  background: 'linear-gradient(160deg, rgba(245,230,208,0.04) 0%, rgba(184,137,90,0.025) 60%, rgba(20,11,7,0.18) 100%)',
  border: '1px solid rgba(245,230,208,0.10)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  boxShadow: 'inset 0 1px 0 rgba(245,230,208,0.07), 0 30px 60px -30px rgba(0,0,0,0.55)',
};

// Eyebrow component "— LABEL —"
export function Eyebrow({ children, centered = false }) {
  return (
    <div className={`flex items-center gap-4 mb-7 ${centered ? 'justify-center' : ''}`}>
      <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
      <span className="text-[10px] uppercase tracking-[0.45em]" style={{ color: COPPER_LIGHT }}>{children}</span>
      {centered && <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />}
    </div>
  );
}

// Premium copper CTA button (use as Link className extras)
export const COPPER_BTN_STYLE = {
  background: COPPER_GRAD,
  color: '#FFF8EC',
  border: '1px solid rgba(245,230,208,0.45)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28), 0 12px 36px -10px rgba(184,137,90,0.55)',
};

// Shimmer span for hover (place inside relative button)
export function Shimmer() {
  return (
    <span
      aria-hidden
      className="absolute inset-y-0 -left-[120%] w-1/2 group-hover:left-[140%] transition-[left] duration-1000 ease-out pointer-events-none"
      style={{ background: 'linear-gradient(120deg, transparent 0%, rgba(255,250,240,0.55) 50%, transparent 100%)', transform: 'skewX(-18deg)' }}
    />
  );
}

// Page hero (dark, centered, eyebrow + serif title with italic word)
export function PageHero({ eyebrow, title, titleEm, description, testid }) {
  return (
    <section
      className="relative pt-24 pb-8 lg:pt-28 lg:pb-10 overflow-hidden"
      style={{ background: DARK_BG }}
      data-testid={testid}
    >
      <CopperHalo x="20%" y="30%" size={900} opacity={0.18} />
      <CopperHalo x="85%" y="70%" size={700} opacity={0.12} />
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.16] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
        <Eyebrow centered>{eyebrow}</Eyebrow>
        <h1
          style={{
            fontFamily: DISPLAY_FF,
            fontSize: 'clamp(2.5rem, 6vw, 5rem)',
            fontWeight: 400,
            color: CREAM,
            letterSpacing: '0.03em',
            lineHeight: 1.05,
          }}
        >
          {title}{' '}
          <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontWeight: 400, fontFamily: ITALIC_FF }}>
            {titleEm}
          </em>
        </h1>
        {description && (
          <p className="mt-7 text-base font-light max-w-2xl mx-auto leading-relaxed" style={{ color: 'rgba(245,230,208,0.65)' }}>
            {description}
          </p>
        )}
      </div>
    </section>
  );
}
